import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  Clock3,
  ExternalLink,
  Menu,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { useState } from "react";
import {
  KNOWLEDGE_BASE_PATH,
  articlePath,
  articles,
  findArticleBySlug,
  type KnowledgeArticle,
} from "./content/articles";

function KnowledgeBrand() {
  return (
    <a className="brand" href="/" aria-label="Amerikolay ana sayfa">
      <span className="brand-orbit" aria-hidden="true">a</span>
      <span>amerikolay</span><b>.</b>
    </a>
  );
}

function KnowledgeHeader() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="site-header">
      <div className="nav-shell">
        <KnowledgeBrand />
        <nav className={menuOpen ? "main-nav is-open" : "main-nav"} aria-label="Ana menü">
          <a href="/#green-card">Green Card</a>
          <a href="/#is-bul">İş bul</a>
          <a href="/#vizeler">Vizeler</a>
          <a href={KNOWLEDGE_BASE_PATH} onClick={() => setMenuOpen(false)}>Rehberler</a>
        </nav>
        <div className="nav-actions">
          <a className="button button-sm desktop-only" href="/#nasil-calisir">Ücretsiz başla <ArrowRight size={16} /></a>
          <button
            className="menu-button"
            aria-label={menuOpen ? "Menüyü kapat" : "Menüyü aç"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>
    </header>
  );
}

function KnowledgeFooter() {
  return (
    <footer>
      <div className="section-shell footer-grid knowledge-footer-grid">
        <div className="footer-brand"><KnowledgeBrand /><p>Amerika&apos;ya giden yolda<br />Türkçe yol arkadaşın.</p></div>
        <div><strong>Rehberler</strong><a href={articlePath(articles[0])}>Green Card</a><a href={articlePath(articles[1])}>Amerika&apos;da iş</a><a href={articlePath(articles[2])}>Vize türleri</a><a href={articlePath(articles[3])}>Vize bülteni</a></div>
        <div><strong>Resmi kaynaklar</strong><a href="https://travel.state.gov" target="_blank" rel="noreferrer">travel.state.gov</a><a href="https://uscis.gov" target="_blank" rel="noreferrer">USCIS</a><a href="https://dol.gov" target="_blank" rel="noreferrer">Department of Labor</a></div>
        <div><strong>Amerikolay</strong><a href="/">Ana sayfa</a><a href={articlePath(articles[5])}>Güvenlik</a><a href="mailto:merhaba@amerikolay.com">İletişim</a></div>
      </div>
      <div className="section-shell footer-bottom">
        <span>© 2026 Amerikolay. Tüm hakları saklıdır.</span>
        <p>Amerikolay bir hukuk bürosu veya devlet kurumu değildir.</p>
      </div>
    </footer>
  );
}

function ArticleCard({ article }: { article: KnowledgeArticle }) {
  return (
    <article className={`knowledge-card tone-${article.tone}`}>
      <div className="knowledge-card-top">
        <span>{article.category}</span>
        <span><Clock3 size={13} /> {article.readMinutes} dk</span>
      </div>
      <h2>{article.shortTitle}</h2>
      <p>{article.description}</p>
      <a href={articlePath(article)}>Rehberi oku <ArrowRight size={16} /></a>
    </article>
  );
}

export function KnowledgeHub() {
  return (
    <main id="top" className="knowledge-page">
      <KnowledgeHeader />
      <section className="knowledge-hero section-shell">
        <div>
          <div className="eyebrow"><BookOpen size={15} /> Amerikolay Bilgi Merkezi</div>
          <h1>Amerika planın için<br /><em>doğru bilgi, net adımlar.</em></h1>
          <p>Green Card, ABD&apos;de iş ve vize süreçlerini resmi kaynaklara dayanan Türkçe rehberlerle adım adım öğren.</p>
        </div>
        <div className="knowledge-hero-note">
          <ShieldCheck />
          <div><strong>Kaynakları görünür bilgi</strong><span>Her rehberde resmi kurum bağlantıları ve son inceleme tarihi bulunur.</span></div>
        </div>
      </section>

      <section className="section-shell knowledge-library" aria-labelledby="knowledge-library-title">
        <div className="knowledge-library-heading">
          <div><span>6 kapsamlı rehber</span><h2 id="knowledge-library-title">Nereden başlamak istersin?</h2></div>
          <p>İhtiyacın olan konuyu seç; kontrol listeleri, kritik uyarılar ve sonraki adımlarla ilerle.</p>
        </div>
        <div className="knowledge-grid">
          {articles.map((article) => <ArticleCard article={article} key={article.slug} />)}
        </div>
      </section>

      <section className="knowledge-method">
        <div className="section-shell knowledge-method-grid">
          <div>
            <div className="eyebrow eyebrow-light"><Sparkles size={15} /> Bilgiyi eyleme dönüştür</div>
            <h2>Okumakla kalma.<br />Kendi yol haritanı çıkar.</h2>
          </div>
          <div className="method-steps">
            <span><b>01</b> Hedefine uygun rehberi oku</span>
            <span><b>02</b> Kontrol listesini tamamla</span>
            <span><b>03</b> Resmi kaynağı son kez doğrula</span>
            <span><b>04</b> Amerikolay ile adımlarını takip et</span>
          </div>
          <a className="button button-light" href="/#nasil-calisir">Ücretsiz yol haritamı çıkar <ArrowRight size={16} /></a>
        </div>
      </section>
      <KnowledgeFooter />
    </main>
  );
}

export function KnowledgeArticlePage({ article }: { article: KnowledgeArticle }) {
  const related = article.relatedSlugs
    .map((slug) => findArticleBySlug(slug))
    .filter((item): item is KnowledgeArticle => Boolean(item));

  return (
    <main id="top" className="knowledge-page article-page">
      <KnowledgeHeader />
      <article>
        <header className={`article-hero tone-${article.tone}`}>
          <div className="section-shell">
            <nav className="breadcrumbs" aria-label="Sayfa yolu">
              <a href="/">Ana sayfa</a><span>/</span><a href={KNOWLEDGE_BASE_PATH}>Rehberler</a><span>/</span><span>{article.category}</span>
            </nav>
            <div className="article-hero-grid">
              <div>
                <span className="article-category">{article.category}</span>
                <h1>{article.title}</h1>
                <p>{article.description}</p>
                <div className="article-meta"><span><Clock3 size={15} /> {article.readMinutes} dakika okuma</span><span>Son inceleme: {article.updated}</span></div>
              </div>
              <aside className="article-outcomes" aria-label="Bu rehberde kazanacakların">
                <strong>Bu rehberin sonunda</strong>
                {article.outcomes.map((outcome) => <span key={outcome}><Check size={15} /> {outcome}</span>)}
              </aside>
            </div>
          </div>
        </header>

        <div className="section-shell article-layout">
          <aside className="article-toc">
            <strong>Bu rehberde</strong>
            {article.sections.map((section) => <a href={`#${section.id}`} key={section.id}>{section.title}</a>)}
            <a className="back-to-library" href={KNOWLEDGE_BASE_PATH}><ArrowLeft size={14} /> Tüm rehberler</a>
          </aside>

          <div className="article-content">
            <div className="article-disclaimer"><ShieldCheck size={18} /><p><strong>Bilgilendirme notu:</strong> Bu içerik genel eğitim amaçlıdır, hukuki tavsiye değildir. Kurallar değişebilir; işlem öncesinde bağlantı verdiğimiz resmi kaynağı kontrol et.</p></div>
            {article.sections.map((section) => (
              <section id={section.id} key={section.id}>
                <h2>{section.title}</h2>
                {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                {section.bullets && <ul>{section.bullets.map((bullet) => <li key={bullet}><Check size={16} /> <span>{bullet}</span></li>)}</ul>}
                {section.callout && <div className="article-callout"><Sparkles size={18} /><p>{section.callout}</p></div>}
              </section>
            ))}

            <section className="official-sources" id="resmi-kaynaklar">
              <span className="article-category">Resmi kaynaklar</span>
              <h2>Bilgiyi kendin doğrula</h2>
              <p>Bu rehber hazırlanırken aşağıdaki birincil kaynaklar esas alındı. Başvuru öncesinde en güncel talimatı her zaman resmi sayfadan kontrol et.</p>
              <div className="source-list">
                {article.sources.map((source) => (
                  <a href={source.href} target="_blank" rel="noreferrer" key={source.href}>
                    <div><strong>{source.label}</strong><span>{source.organization}</span></div><ExternalLink size={16} />
                  </a>
                ))}
              </div>
            </section>
          </div>
        </div>
      </article>

      <section className="related-guides">
        <div className="section-shell">
          <div className="related-heading"><div><span>Sıradaki adım</span><h2>Bu rehberlerle devam et</h2></div><a href={KNOWLEDGE_BASE_PATH}>Tümünü gör <ArrowRight size={15} /></a></div>
          <div className="related-grid">{related.map((item) => <ArticleCard article={item} key={item.slug} />)}</div>
        </div>
      </section>

      <section className="article-cta">
        <div className="section-shell article-cta-inner">
          <div><span>Bilgiden plana</span><h2>Kendi Amerika yol haritanı ücretsiz çıkar.</h2></div>
          <a className="button button-light" href="/#nasil-calisir">Hemen başla <ArrowRight size={16} /></a>
        </div>
      </section>
      <KnowledgeFooter />
    </main>
  );
}
